import { Repository, RepositoryBase } from '@/repository/base';
import LoginParam from '@/models/account/LoginParam';
import PreRegistParam from '@/models/account/PreRegistParam';
import RegisterUserInfo from '@/models/account/RegisterUserInfo';
import IdForgetParam from '@/models/account/IdForgetParam';
import DeleteParam from '@/models/account/DeleteParam';
import MailAddressResetParam from '@/models/account/MailAddressResetParam';
import UpdateUserInfo from '@/models/account/UpdateUserInfo';
import PasswordResetParam from '@/models/account/PasswordResetParam';
import PasswordUpdateParam from '@/models/account/PasswordUpdateParam';
import PasswordResetSettingParam from '@/models/account/PasswordResetSettingParam';

/**
 * 認証系リポジトリアクセス
 */
export default class AccountRepository extends RepositoryBase {
  private repository = '/account';

  /**
   * ログイン
   *
   * @param params LoginParam ログイン情報
   */
  login(params: LoginParam) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.post(`${this.repository}/login`, this.convert.serialize(params), config);
  }

  /**
   * ログアウト
   */
  logout() {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.post(`${this.repository}/logout`, null, config);
  }

  /**
   * 受験者登録
   * @param params registParam 登録情報
   */
  regist(params: RegisterUserInfo) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.post(`${this.repository}`, this.convert.serialize(params), config);
  }

  /**
   * 仮登録
   * @param params PreRegistParam 仮登録情報
   */
  preRegist(params: PreRegistParam) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.post(`${this.repository}/email/confirm`, this.convert.serialize(params), config);
  }

  /**
   * 仮受験者のemailトークンが有効かを取得します。
   *
   * @param token トークン
   */
  emailTokenVerify(token: string) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.get(`${this.repository}/email/${token}/verify`, config);
  }

  /**
   * ログイントークンの有効性検証とログイン情報の取得
   *
   * @param token トークン
   */
  loginTokenVerify(token: string) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.post(`${this.repository}/login_verify`, { token }, config);
  }

  /**
   * 受験者情報取得
   */
  getAccount() {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.get(`${this.repository}`, config);
  }

  /**
   * 対象のユーザIDが存在しているかを取得します。
   *
   * @param userId チェック対象のユーザID
   */
  userIdValidation(userId: string) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    // eslint-disable-next-line @typescript-eslint/camelcase
    return Repository.post(`${this.repository}/id/validation`, { user_id: userId }, config);
  }

  /**
   * ID忘れの通知メール送信
   * @param params IdForgetParam ID忘れ メール送信用情報
   */
  idForget(params: IdForgetParam) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.post(`${this.repository}/id/remind`, this.convert.serialize(params), config);
  }

  /**
   * アカウント削除
   */
  deleteAccount(params: DeleteParam) {
    const config = {
      data: this.convert.serialize(params),
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.delete(`${this.repository}`, config);
  }

  /**
   * メールアドレス再設定メールの送信
   */
  sendMailAddressResetMail(params: MailAddressResetParam) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.post(`${this.repository}/email/reset`, this.convert.serialize(params), config);
  }

  /**
   * 受験者情報更新
   * @param params UpdateUserInfo 更新ユーザー情報
   */
  updateAccount(params: UpdateUserInfo) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.put(`${this.repository}`, this.convert.serialize(params), config);
  }

  /**
   * パスワード再設定メールの送信
   * @param params PasswordResetParam パスワード再設定メール送信用情報
   */
  sendPasswordResetMail(params: PasswordResetParam) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.post(`${this.repository}/password/reset`, this.convert.serialize(params), config);
  }

  /**
   * パスワード再設定
   * @param params PasswordUpdateParam パスワード再設定情報
   */
  passwordUpdate(params: PasswordUpdateParam) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.put(`${this.repository}/password`, this.convert.serialize(params), config);
  }

  /**
   * パスワードリセット
   *
   * @param params PasswordResetSettingParam パスワード再設定情報
   */
  resetPassword(params: PasswordResetSettingParam) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.put(`${this.repository}/password/reset`, this.convert.serialize(params), config);
  }

  /**
   * メールアドレス再設定トークンの有効確認
   *
   * @param _token トークン
   * @param _password パスワード
   */
  emailReset(_token: string, _password: string) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.put(`${this.repository}/email`, { token: _token, password: _password }, config);
  }

  /**
   * 会員情報更新トークンの有効性検証
   *
   * @param token トークン
   */
  verifyUpdateUserInfoToken(token: string) {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.post(`${this.repository}/updateinfo_verify`, { token }, config);
  }

  /**
   * ユーザ情報更新のための認証メール送信
   */
  sendUpdateInfoToken() {
    const config = {
      headers: {
        'x-exam-key': process.env.VUE_APP_EXAM_SECRET_KEY,
      },
    };
    return Repository.get(`${this.repository}/send_updateinfo_token`, config);
  }
}
